import { withCreds } from '../authService';

const ALLOWED_SURFACES = new Set(['public', 'developer']);
const ALLOWED_STAGES = new Set([
  'voice_session_opened',
  'microphone_ready',
  'recognition_ready',
  'speech_started',
  'interruption_candidate',
  'interruption_confirmed',
  'old_turn_invalidated',
  'replacement_turn_accepted',
  'replacement_api_dispatched',
  'replacement_speaking_started',
  'interruption_rejected',
  'playback_paused',
  'playback_resumed',
  'speech_ended',
  'utterance_maximum',
  'partial_transcript',
  'final_transcript',
  'thinking_started',
  'thinking_ui_painted',
  'api_dispatched',
  'api_response',
  'api_response_headers',
  'api_response_body',
  'tts_requested',
  'tts_dispatched',
  'tts_response_received',
  'tts_audio_body_received',
  'first_audio_ready',
  'tts_audio_ready',
  'audio_decoded',
  'route_handoff_started',
  'route_handoff_completed',
  'html_audio_blob_created',
  'html_audio_src_assigned',
  'html_audio_load_invoked',
  'html_audio_load_skipped',
  'html_audio_loadstart',
  'html_audio_loadedmetadata',
  'html_audio_loadeddata',
  'html_audio_canplay',
  'html_audio_canplaythrough',
  'html_audio_play_requested',
  'html_audio_play_resolved',
  'html_audio_play_rejected',
  'html_audio_playing',
  'html_audio_first_positive_time',
  'html_audio_cancelled',
  'playback_requested',
  'playback_started',
  'tts_failed',
  'tts_recovery_listening',
  'tts_recovery_muted',
  'stale_event_ignored',
  'turn_complete',
  'turn_cancelled',
  'transcript_partial',
  'transcript_final',
  'utterance_finalize_requested',
  'utterance_finalized',
  'user_turn_accepted',
  'user_turn_committed',
  'ai_request_started',
  'ai_response_received',
  'model_requested',
  'model_returned',
  'response_validated',
  'speaking_started',
  'speaking_finished',
  'stale_callback_ignored',
  'state_watchdog_triggered',
]);

const LATENCY_SPANS = Object.freeze({
  session_to_microphone_ready: ['voice_session_opened', 'microphone_ready'],
  microphone_to_recognition_ready: ['microphone_ready', 'recognition_ready'],
  speech_to_first_partial: ['speech_started', 'transcript_partial'],
  speech_end_to_final: ['speech_ended', 'utterance_finalized'],
  final_to_api_dispatch: ['utterance_finalized', 'api_dispatched'],
  api_to_response: ['api_dispatched', 'api_response'],
  response_to_tts_dispatch: ['api_response', 'tts_dispatched'],
  tts_to_first_audio: ['tts_dispatched', 'first_audio_ready'],
  audio_ready_to_playback: ['first_audio_ready', 'playback_started'],
  finalized_to_first_audible: ['utterance_finalized', 'playback_started'],
  interruption_to_replacement_dispatch: ['interruption_confirmed', 'api_dispatched'],
  interruption_rejected_to_playback_resumed: ['interruption_rejected', 'playback_resumed'],
});

const SERVER_TIMING_KEYS = Object.freeze([
  'request_to_user_commit_ms',
  'user_commit_to_model_start_ms',
  'model_ms',
  'postprocess_ms',
  'assistant_commit_ms',
  'response_finalize_ms',
  'server_total_ms',
]);

const SERVER_TIMING_HEADER_NAMES = Object.freeze({
  request_to_user_commit_ms: ['br_user_commit', 'user_commit', 'request_to_user_commit'],
  user_commit_to_model_start_ms: ['br_pre_model', 'pre_model', 'user_commit_to_model_start'],
  model_ms: ['br_model', 'model', 'model_generation'],
  postprocess_ms: ['br_postprocess', 'postprocess', 'post_processing'],
  assistant_commit_ms: ['br_assistant_commit', 'assistant_commit', 'assistant_persistence'],
  response_finalize_ms: ['br_finalize', 'response_finalize', 'response_finalization'],
  server_total_ms: ['br_voice', 'br_total', 'server_total', 'total'],
});

const EXPORT_STAGE_ALIASES = Object.freeze({
  speech_end: ['speech_ended'],
  transcript_final: ['utterance_finalized', 'final_transcript', 'transcript_final'],
  turn_accepted: ['user_turn_accepted', 'replacement_turn_accepted'],
  thinking_started: ['thinking_started'],
  thinking_ui_painted: ['thinking_ui_painted'],
  api_dispatched: ['replacement_api_dispatched', 'api_dispatched'],
  tts_requested: ['tts_requested', 'tts_dispatched'],
  tts_response_received: ['tts_response_received', 'first_audio_ready', 'tts_audio_ready'],
  audio_decoded: ['audio_decoded'],
  playback_started: ['playback_started', 'speaking_started'],
  interruption_candidate: ['interruption_candidate'],
  playback_paused: ['playback_paused'],
  first_partial: ['partial_transcript', 'transcript_partial'],
  interruption_confirmed: ['interruption_confirmed'],
  interruption_rejected: ['interruption_rejected'],
  old_turn_invalidated: ['old_turn_invalidated'],
  replacement_turn_accepted: ['replacement_turn_accepted'],
  replacement_api_dispatched: ['replacement_api_dispatched'],
  replacement_speaking_started: ['speaking_started', 'playback_started'],
  playback_resumed: ['playback_resumed'],
});

const EXPORT_SEGMENTS = Object.freeze([
  ['speech_end_to_transcript_final_ms', 'speech_end', 'transcript_final'],
  ['transcript_final_to_turn_accepted_ms', 'transcript_final', 'turn_accepted'],
  ['turn_accepted_to_thinking_started_ms', 'turn_accepted', 'thinking_started'],
  ['turn_accepted_to_thinking_ui_painted_ms', 'turn_accepted', 'thinking_ui_painted'],
  ['thinking_started_to_api_dispatched_ms', 'thinking_started', 'api_dispatched'],
  ['api_dispatched_to_tts_requested_ms', 'api_dispatched', 'tts_requested'],
  ['tts_requested_to_tts_response_received_ms', 'tts_requested', 'tts_response_received'],
  ['tts_response_received_to_audio_decoded_ms', 'tts_response_received', 'audio_decoded'],
  ['audio_decoded_to_playback_started_ms', 'audio_decoded', 'playback_started'],
  ['transcript_final_to_playback_started_ms', 'transcript_final', 'playback_started'],
]);

const MAJOR_REGRESSION_CEILINGS_MS = Object.freeze({
  request_to_user_commit_ms: 3000,
  user_commit_to_model_start_ms: 3000,
  model_ms: 5000,
  postprocess_ms: 2000,
  assistant_commit_ms: 3000,
  response_finalize_ms: 2000,
  server_total_ms: 10000,
});

export function sanitizeVoiceServerTiming(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const timing = {};
  for (const key of SERVER_TIMING_KEYS) {
    const duration = Number(value[key]);
    if (!Number.isFinite(duration) || duration < 0 || duration > 120000) return null;
    timing[key] = Math.round(duration);
  }
  return Object.freeze(timing);
}

export function parseVoiceServerTimingHeader(value) {
  const entries = new Map();
  String(value || '').split(',').forEach((part) => {
    const [rawName, ...parameters] = part.trim().split(';');
    const name = String(rawName || '').trim().toLowerCase();
    if (!name) return;
    const durationParameter = parameters.find((parameter) => /^\s*dur\s*=/i.test(parameter));
    if (!durationParameter) return;
    const duration = Number(durationParameter.split('=').slice(1).join('=').replace(/^"|"$/g, '').trim());
    if (Number.isFinite(duration) && duration >= 0 && duration <= 120000) {
      entries.set(name, Math.round(duration));
    }
  });
  const timing = {};
  for (const [key, aliases] of Object.entries(SERVER_TIMING_HEADER_NAMES)) {
    const alias = aliases.find((name) => entries.has(name));
    if (!alias) return null;
    timing[key] = entries.get(alias);
  }
  return sanitizeVoiceServerTiming(timing);
}

function firstStageTimestamp(stageTimes, aliases) {
  for (const alias of aliases) {
    if (stageTimes.has(alias)) return stageTimes.get(alias);
  }
  return null;
}

export function exportVoiceTimingEvents(events = [], { surface } = {}) {
  const safeSurface = ALLOWED_SURFACES.has(surface) ? surface : null;
  const turns = new Map();
  events.forEach((event) => {
    if (!event || !ALLOWED_SURFACES.has(event.surface) || (safeSurface && event.surface !== safeSurface)) return;
    if (event.event !== 'voice_latency' && event.event !== 'voice_server_latency') return;
    const turn = Number(event.turn || 0);
    if (!Number.isFinite(turn) || turn < 1) return;
    const record = turns.get(turn) || { stages: new Map(), server: null, replacement: false };
    if (event.event === 'voice_latency' && ALLOWED_STAGES.has(event.stage)) {
      const elapsed = Number(event.turn_elapsed_ms);
      if (Number.isFinite(elapsed) && elapsed >= 0 && !record.stages.has(event.stage)) {
        record.stages.set(event.stage, Math.round(elapsed));
      }
      if (/^(interruption_confirmed|old_turn_invalidated|replacement_)/.test(event.stage)) record.replacement = true;
    } else if (event.event === 'voice_server_latency') {
      record.server = sanitizeVoiceServerTiming(event);
    }
    turns.set(turn, record);
  });
  const firstTurn = Math.min(...turns.keys());
  const samples = [...turns.entries()].sort(([left], [right]) => left - right).map(([turn, record]) => {
    const timestamps = Object.fromEntries(Object.entries(EXPORT_STAGE_ALIASES).map(([name, aliases]) => [
      name,
      firstStageTimestamp(record.stages, aliases),
    ]));
    if (!record.replacement) timestamps.replacement_speaking_started = null;
    const segments = Object.fromEntries(EXPORT_SEGMENTS.map(([name, start, end]) => {
      const duration = timestamps[start] === null || timestamps[end] === null
        ? null
        : Math.max(0, timestamps[end] - timestamps[start]);
      return [name, duration];
    }));
    return Object.freeze({
      turn_id: turn,
      sample_classification: record.replacement ? 'replacement' : (turn === firstTurn ? 'cold' : 'warm'),
      timestamps_ms_from_turn_start: Object.freeze(timestamps),
      segments_ms: Object.freeze(segments),
      server_timing_ms: record.server,
    });
  });
  return Object.freeze({
    event: 'voice_timing_export',
    surface: safeSurface || 'all',
    content_included: false,
    samples: Object.freeze(samples),
  });
}

export function classifyVoiceLatencyRegression(value) {
  const timing = sanitizeVoiceServerTiming(value);
  if (!timing) return Object.freeze({ valid: false, major_regressions: [] });
  return Object.freeze({
    valid: true,
    major_regressions: Object.freeze(SERVER_TIMING_KEYS.filter(
      (key) => timing[key] > MAJOR_REGRESSION_CEILINGS_MS[key],
    )),
  });
}

function defaultNow() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function defaultEmit(event) {
  if (typeof console !== 'undefined' && typeof console.info === 'function') {
    // JSON keeps production diagnostics deterministic and copyable. The event
    // schema is allow-listed above and cannot contain user or assistant text.
    console.info('[VOICE_METRIC]', JSON.stringify(event));
  }
}

function defaultExportEmit(report) {
  if (typeof console !== 'undefined' && typeof console.info === 'function') {
    console.info('[VOICE_TIMING_EXPORT]', JSON.stringify(report));
  }
}

function createLatencyRef() {
  const bytes = globalThis.crypto?.getRandomValues
    ? [...globalThis.crypto.getRandomValues(new Uint8Array(8))]
    : Array.from({ length: 8 }, () => Math.floor(Math.random() * 256));
  return `vl_${bytes.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
}

function defaultReportEmit(report) {
  try {
    void Promise.resolve(withCreds('/api/voice/latency', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
      keepalive: true,
    })).catch(() => {});
  } catch (_) {
    // Measurement is fail-open and never owns voice behavior.
  }
}

function parseBoundedTimingHeader(value, prefix = 'br_tts_') {
  const timing = {};
  String(value || '').split(',').forEach((entry) => {
    const [rawName, ...parameters] = entry.trim().split(';');
    const name = String(rawName || '').trim();
    if (!name.startsWith(prefix)) return;
    const durationPart = parameters.find((part) => /^\s*dur\s*=/i.test(part));
    const duration = Number(String(durationPart || '').split('=').slice(1).join('='));
    if (Number.isFinite(duration) && duration >= 0 && duration <= 120000) {
      timing[`${name.slice(prefix.length)}_ms`] = Math.round(duration);
    }
  });
  return timing;
}

export function parseTtsServerTimingHeader(value) {
  const timing = parseBoundedTimingHeader(value);
  return Object.freeze({
    provider_ms: timing.provider_ms ?? null,
    effects_ms: timing.effects_ms ?? null,
    synthesis_ms: timing.synthesis_ms ?? null,
    encode_ms: timing.encode_ms ?? null,
    total_ms: timing.total_ms ?? null,
  });
}

function percentile(values, fraction) {
  if (!values.length) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const index = Math.min(sorted.length - 1, Math.ceil(sorted.length * fraction) - 1);
  return sorted[Math.max(0, index)];
}

// Telemetry correlation must be useful without exposing durable application
// identifiers. This intentionally produces only a short, one-way correlation
// token; raw conversation and voice-session IDs never leave the local tracker.
export function pseudonymousVoiceCorrelation(value, prefix = 'v') {
  const input = String(value || '');
  if (!input) return '';
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${String(prefix || 'v').slice(0, 3)}_${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function summarizeVoiceLatencyEvents(events = [], { surface } = {}) {
  const safeSurface = ALLOWED_SURFACES.has(surface) ? surface : null;
  const stages = {};
  const turns = new Map();
  events.forEach((event) => {
    if (
      event?.event !== 'voice_latency' ||
      !ALLOWED_SURFACES.has(event.surface) ||
      (safeSurface && event.surface !== safeSurface) ||
      !ALLOWED_STAGES.has(event.stage)
    ) return;
    const elapsed = Number(event.since_previous_ms);
    if (!Number.isFinite(elapsed) || elapsed < 0) return;
    (stages[event.stage] ||= []).push(Math.round(elapsed));
    const turnElapsed = Number(event.turn_elapsed_ms);
    if (!Number.isFinite(turnElapsed) || turnElapsed < 0) return;
    const key = `${event.surface}:${Number(event.turn || 0)}`;
    const turnStages = turns.get(key) || new Map();
    if (!turnStages.has(event.stage)) turnStages.set(event.stage, turnElapsed);
    turns.set(key, turnStages);
  });
  const spans = {};
  turns.forEach((turnStages) => {
    Object.entries(LATENCY_SPANS).forEach(([name, [startStage, endStage]]) => {
      if (!turnStages.has(startStage) || !turnStages.has(endStage)) return;
      const duration = turnStages.get(endStage) - turnStages.get(startStage);
      if (!Number.isFinite(duration) || duration < 0) return;
      (spans[name] ||= []).push(Math.round(duration));
    });
  });
  return Object.freeze({
    event: 'voice_latency_summary',
    surface: safeSurface || 'all',
    sample_count: Object.values(stages).reduce((total, values) => total + values.length, 0),
    stages: Object.freeze(Object.fromEntries(Object.entries(stages).map(([stage, values]) => [
      stage,
      Object.freeze({
        count: values.length,
        median_ms: percentile(values, 0.5),
        p95_ms: percentile(values, 0.95),
      }),
    ]))),
    spans: Object.freeze(Object.fromEntries(Object.entries(spans).map(([name, values]) => [
      name,
      Object.freeze({
        count: values.length,
        median_ms: percentile(values, 0.5),
        p95_ms: percentile(values, 0.95),
      }),
    ]))),
  });
}

export function createVoiceLatencyTracker({
  surface,
  now = defaultNow,
  emit = defaultEmit,
  exportEmit = defaultExportEmit,
  reportEmit = defaultReportEmit,
  ownership = () => ({}),
} = {}) {
  const safeSurface = ALLOWED_SURFACES.has(surface) ? surface : 'public';
  let turn = 0;
  let startedAt = null;
  let previousAt = null;
  const observations = [];
  let latencyRef = '';
  let processMetadata = { requestId: '', buildSha: '' };
  let ttsTiming = {};
  let replacement = false;

  const reset = () => {
    turn += 1;
    startedAt = Number(now());
    previousAt = startedAt;
    latencyRef = createLatencyRef();
    processMetadata = { requestId: '', buildSha: '' };
    ttsTiming = {};
    replacement = false;
    return turn;
  };

  return {
    begin() {
      return reset();
    },
    beginIfNeeded() {
      return startedAt === null ? reset() : turn;
    },
    isActive() {
      return startedAt !== null;
    },
    mark(stage) {
      if (!ALLOWED_STAGES.has(stage)) return null;
      if (startedAt === null) reset();
      const observedAt = Number(now());
      const owner = ownership() || {};
      const event = Object.freeze({
        event: 'voice_latency',
        surface: safeSurface,
        stage,
        turn,
        conversation_id: pseudonymousVoiceCorrelation(owner.conversationId, 'c'),
        voice_session_id: pseudonymousVoiceCorrelation(owner.voiceSessionId, 's'),
        generation_id: Number(owner.generationId || 0),
        recognition_generation_id: Number(owner.recognitionGenerationId || 0),
        user_turn_id: Number(owner.userTurnId || 0),
        ai_generation_id: Number(owner.aiGenerationId || owner.generationId || 0),
        since_previous_ms: Math.max(0, Math.round(observedAt - previousAt)),
        turn_elapsed_ms: Math.max(0, Math.round(observedAt - startedAt)),
      });
      previousAt = observedAt;
      observations.push(event);
      if (/^(interruption_confirmed|replacement_)/.test(stage)) replacement = true;
      emit(event);
      return event;
    },
    markIfMissingAfter(stage, afterStage) {
      const current = observations.filter(
        (event) => event.event === 'voice_latency' && event.turn === turn,
      );
      const after = [...current].reverse().find((event) => event.stage === afterStage);
      const existing = [...current].reverse().find((event) => event.stage === stage);
      if (existing && (!after || existing.turn_elapsed_ms >= after.turn_elapsed_ms)) {
        return existing;
      }
      return this.mark(stage);
    },
    currentRef() {
      if (startedAt === null) reset();
      return latencyRef;
    },
    observeProcessMetadata({ requestId = '', buildSha = '' } = {}) {
      processMetadata = {
        requestId: String(requestId || '').slice(0, 96),
        buildSha: String(buildSha || '').slice(0, 96),
      };
    },
    observeTtsTiming(value) {
      ttsTiming = value && typeof value === 'object' ? value : {};
    },
    reportFirstAudible({ browserFamily, appleDeviceClass } = {}) {
      if (startedAt === null || !latencyRef) return null;
      const current = observations.filter((event) => event.event === 'voice_latency' && event.turn === turn);
      const times = new Map();
      current.forEach((event) => {
        times.set(event.stage, event.turn_elapsed_ms);
      });
      const span = (start, end) => (
        times.has(start) && times.has(end) ? Math.max(0, times.get(end) - times.get(start)) : null
      );
      const acceptedStage = replacement ? 'replacement_turn_accepted' : 'utterance_finalized';
      const dispatchStage = replacement ? 'replacement_api_dispatched' : 'api_dispatched';
      const audibleStage = replacement ? 'replacement_speaking_started' : 'playback_started';
      const report = Object.freeze({
        latency_ref: latencyRef,
        browser_family: ['chromium', 'webkit', 'firefox'].includes(browserFamily) ? browserFamily : 'other',
        apple_device_class: ['iphone', 'ipad', 'apple_desktop'].includes(appleDeviceClass)
          ? appleDeviceClass : 'non_apple',
        turn_kind: replacement ? 'replacement' : 'normal',
        temperature: turn === 1 ? 'cold' : 'warm',
        outcome: 'first_audible',
        process_request_id: processMetadata.requestId,
        process_build_sha: processMetadata.buildSha,
        speech_end_to_finalized_ms: span('speech_ended', acceptedStage),
        finalized_to_dispatch_ms: span(acceptedStage, dispatchStage),
        dispatch_to_response_ms: span(dispatchStage, 'api_response_headers'),
        response_to_tts_dispatch_ms: span('api_response_body', 'tts_dispatched'),
        tts_dispatch_to_headers_ms: span('tts_dispatched', 'tts_response_received'),
        tts_headers_to_body_ms: span('tts_response_received', 'first_audio_ready'),
        body_to_route_handoff_ms: span('first_audio_ready', 'route_handoff_started'),
        route_handoff_ms: span('route_handoff_started', 'route_handoff_completed'),
        route_to_play_request_ms: span('route_handoff_completed', 'playback_requested'),
        play_request_to_started_ms: span('playback_requested', audibleStage),
        speech_end_to_first_audible_ms: span('speech_ended', audibleStage),
        process_server_total_ms: [...observations].reverse().find(
          (event) => event.event === 'voice_server_latency' && event.turn === turn,
        )?.server_total_ms ?? null,
        tts_provider_ms: ttsTiming.provider_ms ?? null,
        tts_effects_ms: ttsTiming.effects_ms ?? null,
        tts_encode_ms: ttsTiming.encode_ms ?? null,
        tts_server_total_ms: ttsTiming.total_ms ?? null,
      });
      reportEmit(report);
      return report;
    },
    observeServerTiming(value) {
      const timing = sanitizeVoiceServerTiming(value);
      if (!timing) return null;
      const owner = ownership() || {};
      const event = Object.freeze({
        event: 'voice_server_latency',
        surface: safeSurface,
        turn,
        conversation_id: pseudonymousVoiceCorrelation(owner.conversationId, 'c'),
        voice_session_id: pseudonymousVoiceCorrelation(owner.voiceSessionId, 's'),
        generation_id: Number(owner.generationId || 0),
        ...timing,
        major_regressions: classifyVoiceLatencyRegression(timing).major_regressions,
      });
      observations.push(event);
      emit(event);
      return event;
    },
    cancel() {
      if (startedAt === null) return null;
      const event = this.mark('turn_cancelled');
      exportEmit(exportVoiceTimingEvents(observations, { surface: safeSurface }));
      startedAt = null;
      previousAt = null;
      return event;
    },
    complete() {
      if (startedAt === null) return null;
      const event = this.mark('turn_complete');
      exportEmit(exportVoiceTimingEvents(observations, { surface: safeSurface }));
      startedAt = null;
      previousAt = null;
      return event;
    },
    summary() {
      return summarizeVoiceLatencyEvents(observations, { surface: safeSurface });
    },
    exportTiming() {
      return exportVoiceTimingEvents(observations, { surface: safeSurface });
    },
  };
}
